import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Grid from '@cloudscape-design/components/grid';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import { usePeriod } from '@/lib/period';
import { PeriodControl } from '@/features/shared/PeriodControl';
import { BackfillButton } from '@/features/shared/BackfillButton';
import { OpenInPostHogButton } from '@/components/OpenInPostHogButton';
import { posthogTrendUrl } from '@/lib/posthogLinks';
import { DauWauMauChart } from './DauWauMauChart';
import { StickinessCard } from './StickinessCard';
import { TimeToFirstLabelHistogram } from './TimeToFirstLabelHistogram';
import { useProduct } from './useProduct';

/**
 * Tela `/dashboard/product`: DAU/WAU/MAU e stickiness (PostHog) no período, e
 * o histograma de tempo até a primeira etiqueta.
 *
 * Origem: sem issue — commit 894180bae, 2026-05-04 (Sprint 4.12, ver
 * `docs/sprint-4.12-usage-operation-product.md`).
 *
 * Contrato: sem props; período pelo `usePeriod` (o histograma ignora o
 * período — ver `useProduct`). Estados LOADING/ERROR/READY vêm de
 * `useProduct`. Ações no cabeçalho: abrir no PostHog e recalcular o período
 * (`BackfillButton type="posthog"`, a única escrita).
 *
 * Usado por: `routes/router.tsx` (rota `dashboard/product`, só super-admin).
 *
 * Arquitetura: só apresentação; leitura no `useProduct`, reprocessamento pela
 * callable `backfillAggregations`.
 *
 * Issues: #68 (deep-link PostHog e recalcular).
 */
export function ProductDashboardPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { period, setPeriod } = usePeriod();
  const state = useProduct(period);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('product.subtitle')}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <OpenInPostHogButton
                href={posthogTrendUrl([{ id: '$pageview', math: 'dau' }])}
                surface="product_dau"
              />
              <BackfillButton type="posthog" />
            </SpaceBetween>
          }
        >
          {t('product.title')}
        </Header>
      }
    >
      <SpaceBetween size="l">
        <PeriodControl value={period} onChange={setPeriod} />

        {state.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {state.kind === 'ERROR' && <Alert type="error">{state.error}</Alert>}
        {state.kind === 'READY' && (
          <SpaceBetween size="l">
            <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
              <DauWauMauChart series={state.posthog} />
              <StickinessCard stickinessPct={state.stickinessLatestPct} />
            </Grid>
            <TimeToFirstLabelHistogram buckets={state.timeToFirstLabel} />
            <Container
              header={
                <Header variant="h3" description={t('product.charts.featuresUsedHint')}>
                  {t('product.charts.featuresUsed')}
                </Header>
              }
            >
              <Box textAlign="center" color="text-status-inactive" padding="l">
                {t('product.awaitingInstrumentation')}
              </Box>
            </Container>
          </SpaceBetween>
        )}
      </SpaceBetween>
    </ContentLayout>
  );
}
